import { isEmptyValue } from '@tomo/core';
import { ContextMenu, cx, IconButton, type MenuItem } from '@tomo/ui';
import { AppWindow, Copy, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { usePersonMap, useProject, useReadOnly } from '../../data/hooks.tsx';
import { Icon } from '../../lib/icons.tsx';
import { relativeTime } from '../../lib/time.ts';
import { FieldValue } from '../fields/display.tsx';
import { RecordCard } from './RecordCard.tsx';
import type { CollectionViewProps } from './TableView.tsx';
import { itemNoun } from './view-utils.ts';

export function GalleryView({ collection, view, records, onOpen, onCreate, empty, onUpload }: CollectionViewProps & { onUpload?: () => void }) {
  const project = useProject();
  const readOnly = useReadOnly();
  if (!records.length) return <div className="view-empty">{empty}</div>;
  return (
    <div className="gallery" data-size={view.cardSize}>
      {records.map((r) => (
        <ContextMenu
          key={r.id}
          items={[
            { label: 'Open', icon: <AppWindow />, onSelect: () => onOpen(r.id) },
            ...(readOnly
              ? []
              : ([
                  { label: 'Duplicate', icon: <Copy />, onSelect: () => project.duplicateRecord(r.id) },
                  { type: 'separator' },
                  { label: 'Move to trash', icon: <Trash2 />, danger: true, onSelect: () => project.trashRecord(r.id) },
                ] as MenuItem[])),
          ]}
        >
          <RecordCard
            record={r}
            collection={collection}
            view={view}
            variant="gallery"
            role="button"
            tabIndex={0}
            onClick={() => onOpen(r.id)}
            onKeyDown={(e) => e.key === 'Enter' && onOpen(r.id)}
          />
        </ContextMenu>
      ))}
      {!readOnly && (
        <button type="button" className="gallery__new" onClick={() => (onUpload ? onUpload() : onOpen(onCreate()))}>
          <Plus size={18} />
          <span>{onUpload ? 'Upload files' : `New ${itemNoun(collection)}`}</span>
        </button>
      )}
    </div>
  );
}

/**
 * Document-style list: icon, title, status, owner, tags and last edit. A title is renamed in the row
 * itself (the pencil, F2 or the context menu), so changing one never means opening a window.
 */
export function ListView({ collection, view, records, onOpen, onCreate, empty }: CollectionViewProps) {
  const project = useProject();
  const people = usePersonMap();
  const readOnly = useReadOnly();
  const [renaming, setRenaming] = useState<string | null>(null);
  const hidden = new Set(view.hiddenFields);
  const meta = collection.fields.filter((f) => !hidden.has(f.id) && ['select', 'multiSelect', 'person'].includes(f.type));
  if (!records.length) return <div className="view-empty">{empty}</div>;
  return (
    <div className="doclist">
      {records.map((r) => {
        const editor = r.updatedBy ? people.get(r.updatedBy) : undefined;
        const text = project.getRecordText(r.id).text;
        const excerpt = text && <span className="doclist__excerpt">{text.slice(0, 160)}</span>;
        const editingTitle = renaming === r.id && !readOnly;
        const recordIcon = (
          <span className="doclist__icon" data-color={collection.color}>
            <Icon token={r.icon ?? collection.icon} size={17} />
          </span>
        );
        // The same trailing details either way, so renaming in place leaves the row where it is.
        const details = (
          <>
            <span className="doclist__meta">
              {meta
                .filter((f) => !isEmptyValue(f, r.values[f.id]))
                .map((f) => (
                  <FieldValue key={f.id} record={r} field={f} collection={collection} variant="card" />
                ))}
            </span>
            <span className="doclist__time" title={editor ? `Edited by ${editor.name}` : undefined}>
              {relativeTime(r.updatedAt)}
            </span>
          </>
        );
        return (
          <ContextMenu
            key={r.id}
            // While renaming, right-click belongs to the input: cut, copy, paste.
            disabled={editingTitle}
            items={[
              { label: 'Open', icon: <AppWindow />, onSelect: () => onOpen(r.id) },
              ...(readOnly
                ? []
                : ([
                    { label: 'Rename', icon: <Pencil />, onSelect: () => setRenaming(r.id) },
                    { label: 'Duplicate', icon: <Copy />, onSelect: () => project.duplicateRecord(r.id) },
                    { type: 'separator' },
                    { label: 'Move to trash', icon: <Trash2 />, danger: true, onSelect: () => project.trashRecord(r.id) },
                  ] as MenuItem[])),
            ]}
          >
            <div className="doclist__row" data-renaming={editingTitle || undefined}>
              {editingTitle ? (
                <span className="doclist__line">
                  {recordIcon}
                  <span className="doclist__main">
                    <input
                      className="doclist__input"
                      autoFocus
                      defaultValue={r.title}
                      placeholder={`${collection.titleLabel}…`}
                      aria-label={collection.titleLabel}
                      onFocus={(e) => e.currentTarget.select()}
                      onBlur={(e) => {
                        const value = e.currentTarget.value.trim();
                        if (value !== r.title) project.updateRecord(r.id, { title: value });
                        setRenaming(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') {
                          e.currentTarget.value = r.title;
                          e.currentTarget.blur();
                        }
                      }}
                    />
                    {excerpt}
                  </span>
                  {details}
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    className="doclist__line"
                    onClick={() => onOpen(r.id)}
                    onKeyDown={(e) => {
                      if (readOnly || e.key !== 'F2') return;
                      e.preventDefault();
                      setRenaming(r.id);
                    }}
                  >
                    {recordIcon}
                    <span className="doclist__main">
                      <span className={cx('doclist__title', !r.title && 'value-empty')}>{r.title || 'Untitled'}</span>
                      {excerpt}
                    </span>
                    {details}
                  </button>
                  {!readOnly && (
                    <IconButton
                      className="doclist__edit"
                      size="sm"
                      label={`Rename ${r.title || 'Untitled'}`}
                      tooltip="Rename"
                      tooltipShortcut="F2"
                      onClick={() => setRenaming(r.id)}
                    >
                      <Pencil />
                    </IconButton>
                  )}
                </>
              )}
            </div>
          </ContextMenu>
        );
      })}
      {!readOnly && (
        <button type="button" className="doclist__new" onClick={() => onOpen(onCreate())}>
          <Plus size={15} /> New {itemNoun(collection)}
        </button>
      )}
    </div>
  );
}
